<template>
  <v-card class="hmrag-chat elevation-4">
    <v-toolbar dark color="secondary">
      <v-icon class="mr-2">question_answer</v-icon>
      <v-toolbar-title>Healthmark Product Assistant</v-toolbar-title>
    </v-toolbar>

    <v-card-text class="chat-body">
      <section v-if="messages.length === 0" class="chat-intro">
        <strong>Answers come from Healthmark product documents</strong>
        <div>Ask about instructions for use, safety information, specifications, or product details.</div>
      </section>

      <div
        ref="messageLog"
        class="message-log"
        aria-live="polite"
      >
        <div
          v-for="(message, index) in messages"
          :key="index"
          :class="['message', message.role]"
          :data-message-index="index"
        >
          <div v-if="message.role === 'user'" class="message-content">{{ message.content }}</div>
          <ctlHmRagAnswer
            v-else
            :html="message.answerHtml"
            @citation="openCitationEvidence($event, message)"
          />

          <div v-if="message.role === 'assistant' && message.citationGroups && message.citationGroups.length" class="citations">
            <span>Sources: </span>
            <template v-for="(group, groupIndex) in message.citationGroups">
              <span :key="`separator-${groupIndex}`" v-if="groupIndex"> · </span>
              <a
                :key="groupIndex"
                v-if="group.url"
                :href="group.url"
                target="_blank"
                rel="noopener noreferrer"
              >[{{ group.numbers.join(', ') }}] {{ group.label }}</a>
              <span :key="`label-${groupIndex}`" v-else>[{{ group.numbers.join(', ') }}] {{ group.label }}</span>
            </template>
          </div>

          <div v-if="message.role === 'assistant' && message.productWiki && message.productWiki.length" class="citations">
            <span>Product Wiki: </span>
            <template v-for="(link, linkIndex) in message.productWiki">
              <span :key="`wiki-separator-${linkIndex}`" v-if="linkIndex"> &middot; </span>
              <!-- A new tab, NOT a router-link. This component holds the conversation in its own
                   `messages` data and nothing keeps it alive across a route change (no keep-alive,
                   and Vuex holds app state, not chat), so navigating in place would throw the
                   customer's conversation away. $router.resolve turns the route path into a real
                   href, which also picks up BASE_URL when the app is mounted under a sub-path. -->
              <a
                :key="`wiki-${linkIndex}`"
                :href="$router.resolve(link.href).href"
                target="_blank"
                rel="noopener noreferrer"
              >{{ link.name }}</a>
            </template>
          </div>

          <div v-if="message.role === 'assistant' && message.latencyMs != null" class="answer-meta">
            <v-icon size="14">access_time</v-icon> {{ (message.latencyMs / 1000).toFixed(1) }}s
          </div>

          <div v-if="message.role === 'assistant'" class="answer-footer">
            <span v-if="message.queryId && !message.ratingSubmitted" class="rating-inner">
              <span class="rating-label">Was this answer helpful?</span>
              <span class="stars" @mouseleave="message.hoveredRating = 0">
                <button
                  v-for="star in 5"
                  :key="star"
                  type="button"
                  class="star-button"
                  :aria-label="`${star} star rating`"
                  @mouseenter="message.hoveredRating = star"
                  @click="selectRating(message, star)"
                >{{ ratingGlyph(message, star) }}</button>
              </span>
            </span>
            <span v-else-if="message.queryId" class="rating-thanks">
              Rated {{ '★'.repeat(message.rating) }}{{ '☆'.repeat(5 - message.rating) }} · Thanks for your feedback
            </span>

            <button
              type="button"
              class="copy-button"
              :class="{ ok: message.copyState === 'ok' }"
              title="Copy this answer"
              @click="copyAnswer(message)"
            >
              <v-icon size="13">content_copy</v-icon>
              <span>{{ copyLabel(message) }}</span>
            </button>

            <div v-if="message.queryId && message.rating && !message.ratingSubmitted" class="rating-panel">
              <v-textarea
                v-model="message.comment"
                :label="ratingCommentLabel(message)"
                :error-messages="message.ratingError ? [message.ratingError] : []"
                rows="2"
                auto-grow
                outline
                maxlength="2000"
                @input="message.ratingError = ''"
              />
              <v-btn
                small
                color="primary"
                class="white--text rating-submit"
                :loading="message.ratingLoading"
                :disabled="message.ratingLoading || (message.rating <= 3 && !message.comment.trim())"
                @click="submitRating(message)"
              >Submit feedback</v-btn>
            </div>
          </div>
        </div>

        <div v-if="sending" class="message assistant thinking">
          <em>Reading the source documents… this usually takes under a minute.</em>
        </div>
      </div>

      <v-alert v-if="error" :value="true" type="error" outline class="chat-error">
        {{ error }}
      </v-alert>

      <form class="composer" :class="{ focused: focused }" @submit.prevent="send">
        <textarea
          v-model="question"
          rows="1"
          maxlength="8000"
          autocomplete="off"
          placeholder="Ask about an IFU, product specification, or safety information…"
          aria-label="Question for the Healthmark Product Assistant"
          @focus="focused = true"
          @blur="focused = false"
          @input="resizeComposer"
          @keydown.enter.exact.prevent="send"
        />
        <div class="composer-toolbar">
          <span class="composer-hint">Enter to send · Shift+Enter for a new line</span>
          <span class="char-count">{{ question.length }} chars</span>
          <button class="send-button" type="submit" aria-label="Send question" :disabled="!canSend">
            <v-icon color="white">arrow_upward</v-icon>
          </button>
        </div>
      </form>
    </v-card-text>
    <ctlHmRagEvidencePopup ref="evidence" />
  </v-card>
</template>

<script>
import hmRagService from '@/services/hmRag.service'
import { groupCitations, productWikiLinks, renderChatAnswer } from '@/services/hmRagContent'
import { buildAnswerClipboardPayload, copyButtonLabel, writeClipboard } from '@/services/hmRagClipboard'
import ctlHmRagAnswer from '@/components/ctlHmRagAnswer'
import ctlHmRagEvidencePopup from '@/components/ctlHmRagEvidencePopup'

export default {
  components: { ctlHmRagAnswer, ctlHmRagEvidencePopup },
  data: () => ({
    question: '',
    messages: [],
    sending: false,
    focused: false,
    error: ''
  }),
  computed: {
    canSend() {
      return !this.sending && this.question.trim().length > 0
    }
  },
  methods: {
    async send() {
      if (!this.canSend) return
      const question = this.question.trim()
      this.messages.push({ role: 'user', content: question })
      this.question = ''
      this.error = ''
      this.sending = true
      this.$nextTick(this.scrollToBottom)

      try {
        const response = await hmRagService.chat(question)
        const data = response.data || {}
        const assistantMessage = {
          role: 'assistant',
          answerHtml: renderChatAnswer(
            data.answer_html || '<p>No answer was returned.</p>',
            data.citations || [],
            data.marker_claims || []
          ),
          citations: data.citations || [],
          citationGroups: groupCitations(data.citations || []),
          // Declared here, at creation: Vue 2 cannot detect a property added to a message
          // object afterwards, so the line would never render if this were assigned later.
          productWiki: productWikiLinks(data.product_wiki),
          evidence: data.evidence || null,
          copyState: '',
          latencyMs: data.latency_ms,
          queryId: data.query_id,
          rating: 0,
          hoveredRating: 0,
          comment: '',
          ratingLoading: false,
          ratingSubmitted: false,
          ratingError: ''
        }
        this.messages.push(assistantMessage)
      } catch (error) {
        const responseData = error.response && error.response.data
        this.error = (responseData && responseData.error) || 'The product assistant is temporarily unavailable. Please try again.'
      } finally {
        this.sending = false
        this.$nextTick(this.scrollToBottom)
      }
    },
    selectRating(message, rating) {
      message.rating = rating
      message.ratingError = ''
    },
    ratingGlyph(message, star) {
      return star <= (message.hoveredRating || message.rating) ? '★' : '☆'
    },
    ratingCommentLabel(message) {
      return message.rating <= 3 ? 'Tell us what went wrong (required)' : 'Additional comments (optional)'
    },
    async submitRating(message) {
      if (message.rating <= 3 && !message.comment.trim()) {
        message.ratingError = 'Please tell us what could be improved.'
        return
      }
      message.ratingLoading = true
      message.ratingError = ''
      try {
        await hmRagService.rateAnswer(message.queryId, message.rating, message.comment.trim())
        message.ratingSubmitted = true
      } catch (error) {
        message.ratingError = 'Could not submit feedback. Please try again.'
      } finally {
        message.ratingLoading = false
      }
    },
    copyLabel(message) {
      return copyButtonLabel(message.copyState)
    },
    async copyAnswer(message) {
      const payload = buildAnswerClipboardPayload(message.answerHtml, message.citations)
      const copied = await writeClipboard(payload.html, payload.text)
      message.copyState = copied ? 'ok' : 'failed'
      setTimeout(() => { message.copyState = '' }, 1500)
    },
    openCitationEvidence(reference, message) {
      const anchor = reference.anchor
      const citationNumber = reference.citationNumber
      const claimIndex = reference.claimIndex
      const claims = (message.evidence && message.evidence.claims) || []
      const pool = claimIndex != null && claims[claimIndex] ? [claims[claimIndex]] : claims
      const citation = (message.citations || []).find(item => Number(item.number) === citationNumber)
      const quotes = []
      pool.forEach(claim => {
        const claimEvidence = claim.evidence || []
        claimEvidence.forEach(item => {
          if (Number(item.n) === citationNumber && item.located !== false && item.quote) {
            quotes.push({
              quote: item.quote,
              source_url: citation && citation.source_url,
              doc_type: citation && citation.doc_type,
              label: citation && citation.label
            })
          }
        })
      })
      this.$refs.evidence.open(anchor, { citationNumber, citation, quotes })
    },
    resizeComposer(event) {
      const textarea = event.target
      textarea.style.height = 'auto'
      textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`
    },
    scrollToBottom() {
      const log = this.$refs.messageLog
      if (log) log.scrollTop = log.scrollHeight
    }
  }
}
</script>

<style scoped>
.hmrag-chat { margin-bottom: 24px; }
.chat-body { padding: 20px; }
.chat-intro { background: #f7f9fb; border: 1px solid #e3e8ee; border-left: 3px solid #1558d6; border-radius: 10px; color: #3d4a58; font-size: 13.5px; line-height: 1.55; margin-bottom: 16px; padding: 12px 14px; }
.chat-intro strong { color: #1c2530; display: block; margin-bottom: 4px; }
.message-log { display: flex; flex-direction: column; gap: 12px; max-height: 520px; overflow-y: auto; padding: 2px; }
.message { border-radius: 10px; padding: 10px 12px; }
.message.user { align-self: flex-end; background: #e8f0fe; max-width: 80%; white-space: pre-wrap; }
.message.assistant { align-self: flex-start; background: #f3f5f7; max-width: 90%; width: auto; }
.message-content { line-height: 1.55; white-space: pre-wrap; }
.hmrag-markdown { white-space: normal; }
.hmrag-markdown >>> p { margin: 0 0 9px; }
.hmrag-markdown >>> p:last-child { margin-bottom: 0; }
.hmrag-markdown >>> ol, .hmrag-markdown >>> ul { margin: 7px 0; padding-left: 25px; }
.hmrag-markdown >>> a.evi { color: #1558d6; font-weight: 600; text-decoration: none; }
.hmrag-markdown >>> a.evi:hover { text-decoration: underline; }
.thinking { color: #6b7785; }
.citations { border-top: 1px solid #e0e4e8; color: #5a6470; font-size: 13px; margin-top: 10px; padding-top: 8px; }
.citations a { color: #1558d6; text-decoration: none; }
.citations a:hover { text-decoration: underline; }
.answer-meta { color: #8a9aaa; font-size: 12px; margin-top: 6px; }
.answer-footer { align-items: center; border-top: 1px solid #e0e4e8; color: #5a6470; display: flex; flex-wrap: wrap; gap: 8px; margin-top: 10px; padding-top: 8px; }
.rating-inner { align-items: center; display: inline-flex; gap: 8px; }
.rating-label { color: #6b7785; font-size: 13px; }
.stars { display: inline-flex; }
.star-button { background: transparent; border: 0; color: #f5a623; cursor: pointer; font-size: 21px; line-height: 1; padding: 1px; }
.rating-panel { flex-basis: 100%; margin-top: 4px; }
.rating-submit { float: right; margin-top: -8px; }
.rating-thanks { color: #1a7a30; font-size: 13px; }
.copy-button { align-items: center; background: #fff; border: 1px solid #d9dde2; border-radius: 8px; color: #5a6470; cursor: pointer; display: inline-flex; font: inherit; font-size: 12.5px; gap: 5px; line-height: 1.2; margin-left: auto; padding: 4px 10px; transition: border-color .15s, color .15s; }
.copy-button:hover { border-color: #6b7785; color: #1c2530; }
.copy-button.ok { border-color: #9fd3ab; color: #1a7a30; }
.chat-error { margin: 14px 0 0; }
.composer { background: #fff; border: 1px solid #d9dde2; border-radius: 16px; box-shadow: 0 1px 2px rgba(20, 30, 45, .05); margin-top: 16px; padding: 12px 14px 10px; transition: border-color .15s, box-shadow .15s; }
.composer.focused { border-color: #1558d6; box-shadow: 0 0 0 3px rgba(21, 88, 214, .12); }
.composer textarea { background: transparent; border: 0; color: #1c2530; display: block; font: inherit; line-height: 1.5; max-height: 200px; min-height: 48px; outline: 0; overflow-y: auto; resize: none; width: 100%; }
.composer textarea::placeholder { color: #8a9aaa; }
.composer-toolbar { align-items: center; display: flex; gap: 10px; justify-content: flex-end; margin-top: 6px; }
.composer-hint { color: #8a9aaa; font-size: 12px; margin-right: auto; }
.char-count { color: #8a9aaa; font-size: 12px; }
.char-count.over { color: #b45309; }
.send-button { align-items: center; background: #1558d6; border: 0; border-radius: 50%; cursor: pointer; display: flex; flex: 0 0 36px; height: 36px; justify-content: center; transition: background .15s, transform .05s; width: 36px; }
.send-button:hover { background: #1147b0; }
.send-button:active { transform: scale(.94); }
.send-button:disabled { background: #c7ccd1; cursor: default; }
@media (max-width: 600px) { .chat-body { padding: 14px; } .message.user, .message.assistant { max-width: 95%; } .composer-hint { display: none; } }
</style>
